import { useRef, type ReactNode } from 'react';

/**
 * The ONE stateful bottom sheet for Global live at phone width (slice 84), mirroring
 * the tenant pattern in web/src/features/live/BottomSheet.tsx. Two floating panels
 * over a phone-width map are unusable, so a single sheet holds the search + list
 * (collapsed peek → expanded) and, on selection, collapses to a peek showing the
 * selected vehicle's card. Indigo-neutral chrome (admin), not the green web tokens.
 *
 * A drag handle toggles expand/collapse on a short vertical drag; the TAP is an
 * `onClick` (so keyboard/Enter-Space genuinely toggles it, the 17t lesson), and
 * `handledByDrag` stops a real drag being toggled twice.
 *
 * This is a SMALL admin component rather than a shared one: the tenant BottomSheet
 * depends on web-only tokens (`bg-raad-deep`, `--raad-sheet-shadow`) and
 * `usePublishSheetHeight`, none of which exist in admin — porting the ~40 lines of
 * drag logic is cheaper and keeps the package free of app chrome.
 */
export function BottomSheet({
  expanded,
  onExpandedChange,
  label,
  expandLabel,
  collapseLabel,
  collapsedPx,
  children,
}: {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  label: string;
  expandLabel: string;
  collapseLabel: string;
  /** The collapsed detent in px — derived from the content the sheet is holding. */
  collapsedPx: number;
  children: ReactNode;
}) {
  const dragStart = useRef<number | null>(null);
  const handledByDrag = useRef(false);

  function onPointerDown(e: React.PointerEvent) {
    dragStart.current = e.clientY;
    handledByDrag.current = false;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }
  function onPointerUp(e: React.PointerEvent) {
    if (dragStart.current == null) return;
    const dy = e.clientY - dragStart.current;
    dragStart.current = null;
    if (dy > 36) {
      handledByDrag.current = true;
      onExpandedChange(false);
    } else if (dy < -36) {
      handledByDrag.current = true;
      onExpandedChange(true);
    }
  }
  function onClick() {
    if (handledByDrag.current) {
      handledByDrag.current = false;
      return;
    }
    onExpandedChange(!expanded);
  }

  return (
    <div
      data-testid="admin-live-sheet"
      className={[
        'pointer-events-auto absolute inset-x-0 bottom-0 z-30 flex flex-col rounded-t-2xl',
        'border-t border-raad-border bg-raad-panel shadow-2xl',
        'transition-[max-height] duration-200 ease-out',
      ].join(' ')}
      // Runtime value (the collapsed detent depends on the content), so inline, not
      // a compiled Tailwind arbitrary value.
      style={{ maxHeight: expanded ? '72dvh' : `${collapsedPx}px` }}
      role="region"
      aria-label={label}
    >
      <button
        type="button"
        data-testid="admin-sheet-handle"
        aria-expanded={expanded}
        aria-label={expanded ? collapseLabel : expandLabel}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onClick={onClick}
        className="flex flex-none touch-none items-center justify-center py-2.5"
      >
        <span className="h-1 w-9 rounded-full bg-raad-border" aria-hidden="true" />
      </button>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </div>
  );
}
